// src/components/SurgeryDetailModal.tsx

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, ModalContent, CloseButton, Button, Input } from '../styles/GlobalStyles';
import { 
  updateSurgery, 
  getQuestionsByPatientAndDoctor, 
  getPatientSessions,
  addPatientSession,
  updatePatientSession
} from '../services/firebase';
import { Surgery, Patient, Doctor, Question as QuestionType, PatientSession } from '../types';
import SurgeryFormModal from './SurgeryFormModal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from '../i18n';
import { useFormatDate } from '../hooks/useFormatDate';

interface SurgeryDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  surgery: Surgery;
  patients: Patient[];
  doctors: Doctor[];
  onSurgeryUpdated: () => void;
}

const ContentArea = styled.div`
  display: flex;
  gap: 24px;
  max-height: 70vh;
`;

const LeftPanel = styled.div`
  width: 250px;
  border-right: 1px solid #e5e7eb;
  padding-right: 24px;
`;

const RightPanel = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const SectionInfo = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
`;

const PatientList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const PatientItem = styled.div<{ isSelected?: boolean }>`
  border: 1px solid ${props => props.isSelected ? '#0369a1' : '#e5e7eb'};
  border-radius: 8px;
  padding: 8px 16px;
  margin-bottom: 12px;
  background-color: ${props => props.isSelected ? '#f0f9ff' : 'white'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.isSelected ? '#f0f9ff' : '#f9fafb'};
    border-color: #0369a1;
  }
`;

const PatientHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const PatientName = styled.div`
  flex-shrink: 0;
  font-weight: 600;
  color: #1f2937;
`;

const DoctorSelect = styled.select`
  padding: 3px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 10px;
  background-color: white;
  
  &:focus {
    border-color: #0369a1;
    box-shadow: 0 0 0 2px rgba(3, 105, 161, 0.1);
    outline: none;
  }
`;

const LinkButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const CopyButton = styled.button`
  font-size: 10px;
  color: #0369a1;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  
  &:hover {
    background-color: #f0f9ff;
    text-decoration: underline;
  }
`;

const OpenLinkButton = styled.button`
  font-size: 10px;
  color: #166534;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  
  &:hover {
    background-color: #ecfdf5;
    text-decoration: underline;
  }
`;

const EyeButton = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: #0369a1;
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    background-color: #f3f4f6;
    border-radius: 4px;
  }
`;

const SearchInput = styled(Input)`
  margin-bottom: 16px;
`;

const AddPatientSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`;

const AvailablePatientsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;

const AvailablePatientItem = styled.div`
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f9fafb;
    border-color: #0369a1;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const SmallButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;
  
  ${props => props.variant === 'primary' 
    ? `
      background-color: #0369a1;
      color: white;
      border-color: #0369a1;
      &:hover { background-color: #5856eb; }
    `
    : `
      background-color: white;
      color: #6b7280;
      border-color: #d1d5db;
      &:hover { background-color: #f9fafb; }
    `
  }
`;

const MarkdownContainer = styled.div`
  color: #6b7280;
  line-height: 1.5;
  & ul {
    padding-left: 1.2em;
    margin-bottom: 1em;
  }
  & ol {
    padding-left: 1.2em;
    margin-bottom: 1em;
  }
  & h4 {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    margin-top: 1em;
  }
  & p {
    margin-bottom: 0.8em;
  }
`;

const SectionList = styled.div`
  margin-bottom: 24px;
`;

const SectionItem = styled.div`
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
`;

const SectionItemTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
`;

const AttachmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  position: relative;
`;

const AttachmentItem = styled.div`
  background-color: #ddd6fe;
  color: #7c3aed;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #c4b5fd;
    transform: translateY(-1px);
  }
`;

// 질문/답변 관련 스타일들
const QAContainer = styled.div`
  height: 100%;
`;

const QAHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
`;

const PatientNameHeader = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
`;

const QAStats = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const QAList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const QASetItem = styled.div`
  background-color: #f9fafb;
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #0369a1;
  border: 1px solid #e5e7eb;
`;

const QASetHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 16px;
`;

const QASetNumber = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #0369a1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 16px;
`;

const QuestionSection = styled.div`
  margin-bottom: 16px;
`;

const AnswerSection = styled.div``;

const QLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #0369a1;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ALabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #166534;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const QText = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.5;
  background-color: #eff6ff;
  padding: 12px;
  border-radius: 8px;
  border-left: 3px solid #0369a1;
`;

const AText = styled.div`
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  background-color: #f0fdf4;
  padding: 12px;
  border-radius: 8px;
  border-left: 3px solid #166534;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  line-height: 1.5;
`;

const BackButton = styled.button`
  color: #0369a1;
  font-size: 14px;
  font-weight: 500;
  background: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

// 질문/답변 세트 인터페이스
interface QASet {
  question: string;
  answer: string;
  index: number;
}

const SPECIAL_CONDITION_LABELS: Record<string, string> = {
  resectionType: 'patientCond.resectionType',
  cancerStage: 'patientCond.cancerStage',
  reconstructionType: 'patientCond.reconstructionType',
  otherNotes: 'patientCond.otherNotes',
};

const SurgeryDetailModal: React.FC<SurgeryDetailModalProps> = ({ isOpen, onClose, surgery, patients, doctors, onSurgeryUpdated }) => {
  const { t } = useTranslation();
  const { formatDate } = useFormatDate();

  const [searchTerm, setSearchTerm] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [availablePatients, setAvailablePatients] = useState<Patient[]>([]);
  const [selectedNewPatients, setSelectedNewPatients] = useState<string[]>([]);
  const [selectedPatientForQA, setSelectedPatientForQA] = useState<string | null>(null);
  const [selectedPatientQuestions, setSelectedPatientQuestions] = useState<QuestionType | null>(null);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [patientSessions, setPatientSessions] = useState<PatientSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAvailablePatients();
      loadPatientSessions();
      setSelectedPatientForQA(null);
      setSelectedPatientQuestions(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, surgery, patients]);

  // 🔥 수정된 환자 세션 로드 함수 - patientName 사용
  const loadPatientSessions = async () => {
    if (!surgery) return;
    
    setLoadingSessions(true);
    try {
      const allSessions: PatientSession[] = [];
      
      for (const patientId of surgery.patientIds) {
        const patient = patients.find(p => p.id === patientId);
        if (patient) {
          const sessions = await getPatientSessions(surgery.institutionNumber, patient.name); // 🔥 수정
          const relevantSessions = sessions.filter(session => session.surgeryName === surgery.name);
          allSessions.push(...relevantSessions);
        }
      }
      
      setPatientSessions(allSessions);
    } catch (error) {
      console.error('환자 세션 로드 오류:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadAvailablePatients = () => {
    const available = patients.filter(patient => !surgery.patientIds.includes(patient.id));
    setAvailablePatients(available);
  };

  const getPatientInfo = (patientId: string) => {
    return patients.find(patient => patient.id === patientId);
  };

  // 🔥 수정된 환자의 세션 정보 가져오기 - patientName 사용
  const getPatientSession = (patientName: string) => {
    return patientSessions.find(session => 
      session.patientName === patientName &&
      session.surgeryName === surgery.name
    );
  };

  // 🔥 수정된 의사 배정 변경 함수 - patientName 사용
  const handleDoctorAssignment = async (patientName: string, doctorId: string) => {
    try {
      let session = getPatientSession(patientName);
      const patient = patients.find(p => p.name === patientName);
      
      if (!patient) return;

      if (session) {
        await updatePatientSession(session.id, { doctorId: doctorId || 'general' });
      } else {
        const newSessionData: Omit<PatientSession, 'id'> = {
          patientName,
          institutionNumber: surgery.institutionNumber,
          surgeryName: surgery.name,
          doctorId: doctorId || 'general',
          createdAt: new Date(),
          updatedAt: new Date()
        };
        await addPatientSession(newSessionData);
      }
      
      await loadPatientSessions();
      
    } catch (error) {
      console.error('의사 배정 변경 오류:', error);
      alert(t('surgeryDetail.alerts.doctor_assign_failed'));
    }
  };

  // 🔥 수정된 링크 생성 함수 - patientName 사용
  const generatePatientLink = (patientId: string) => {
    const patient = getPatientInfo(patientId);
    const session = patient ? getPatientSession(patient.name) : null;
    
    if (!patient) return '';
    
    const baseUrl = window.location.origin;
    const encodedSurgeryName = encodeURIComponent(surgery.name);
    
    if (session && session.doctorId && session.doctorId !== 'general') {
      return `${baseUrl}/patient/${encodedSurgeryName}/${patient.name}/${session.doctorId}`;
    } else {
      return `${baseUrl}/patient/${encodedSurgeryName}/${patient.name}`;
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert(t('surgeryDetail.alerts.copy_success'));
    } catch (error) {
      console.error('복사 실패:', error);
      alert(t('surgeryDetail.alerts.copy_failed'));
    }
  };

  const openLink = (url: string) => {
    window.open(url, '_blank');
  };

  // 🔥 수정된 QA 클릭 핸들러 - patientName 사용
  const handlePatientEyeClick = async (patientId: string) => {
    const patient = getPatientInfo(patientId);
    const session = patient ? getPatientSession(patient.name) : null;
    
    if (!patient) return;

    setSelectedPatientForQA(patientId);
    setLoadingQuestions(true);

    try {
      console.log('🔍 QA 데이터 로딩 시작:', {
        patient: patient.name,
        surgeryName: surgery.name,
        institutionNumber: surgery.institutionNumber,
        doctorId: session?.doctorId
      });

      const doctorId = session?.doctorId && session.doctorId !== 'general' ? session.doctorId : undefined;
      
      const questions = await getQuestionsByPatientAndDoctor(
        surgery.name, 
        surgery.institutionNumber,
        patient.name, // 🔥 수정: patientNumber → name
        doctorId
      );
      
      setSelectedPatientQuestions(questions);
      
      console.log('✅ QA 데이터 로딩 완료:', {
        found: !!questions,
        questionsCount: questions?.questions?.length || 0,
        answersCount: questions?.answers?.length || 0
      });
      
    } catch (error) {
      console.error('❌ QA 데이터 로드 오류:', error);
      setSelectedPatientQuestions(null);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // 🔥 수정된 필터링 - name으로 검색
  const filteredAvailablePatients = availablePatients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddPatients = async () => {
    if (selectedNewPatients.length === 0) return;

    try {
      const updatedPatientIds = [...surgery.patientIds, ...selectedNewPatients];
      await updateSurgery(surgery.id, { patientIds: updatedPatientIds });
      
      setSelectedNewPatients([]);
      setSearchTerm('');
      onSurgeryUpdated();
      window.location.reload();
    } catch (error) {
      console.error('환자 추가 오류:', error);
      alert('환자 추가 중 오류가 발생했습니다.');
    }
  };

  const toggleNewPatientSelection = (patientId: string) => {
    setSelectedNewPatients(prev => {
      if (prev.includes(patientId)) {
        return prev.filter(id => id !== patientId);
      } else {
        return [...prev, patientId];
      }
    });
  };

  // 질문/답변을 세트로 변환하는 함수
  const convertToQASets = (questions: QuestionType): QASet[] => {
    if (!questions || !questions.questions || !questions.answers) return [];
    
    return questions.questions.map((question, index) => ({
      question,
      answer: questions.answers[index] || '답변이 기록되지 않았습니다.',
      index: index + 1
    }));
  };

  function formatSpecialConditions(conds: any) {
    if (!conds) return null;
    const lines: string[] = [];
    Object.entries(SPECIAL_CONDITION_LABELS).forEach(([key, labelKey]) => {
      const val = conds[key as keyof typeof conds];
      if (typeof val === 'string' && val.trim()) {
        const translatedLabel = t(labelKey);
        lines.push(`${translatedLabel}: ${val}`);
      }
    });
    if (lines.length === 0) return <span>{t('surgeryDetail.rightPanel.qa_conditions_none')}</span>;
    return (
      <ul style={{ paddingLeft: '1em', margin: 0 }}>
        {lines.map((line, i) => <li key={i} style={{ marginBottom: '4px' }}>{line}</li>)}
      </ul>
    );
  }

  const renderRightPanelContent = () => {
    if (!selectedPatientForQA) {
      return (
        <>
          <SectionInfo>
            <SectionTitle>{t('surgeryDetail.rightPanel.info_title')}</SectionTitle>
            <div style={{ marginBottom: '4px', color: '#6b7280' }}>
              {t('surgeryDetail.rightPanel.info_createdAt', { date: formatDate(surgery.createdAt) })}
            </div>
            <div style={{ marginBottom: '4px', color: '#6b7280' }}>
              {t('surgeryDetail.rightPanel.info_updatedAt', { date: formatDate(surgery.updatedAt) })}
            </div>
            <div style={{ color: '#6b7280' }}>
              {t('surgeryDetail.rightPanel.info_sectionCount', { count: surgery.sections.length })}
            </div>
          </SectionInfo>

          <SectionTitle>{t('surgeryDetail.rightPanel.sectionList_title')}</SectionTitle>
          <SectionList>
            {surgery.sections.map((section) => (
              <SectionItem key={section.id}>
                <SectionItemTitle>{section.title}</SectionItemTitle>
                <MarkdownContainer>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ node, ...props }) => (
                        <a {...props} target="_blank" rel="noopener noreferrer">
                          {props.children}
                        </a>
                      ),
                    }}
                  >
                    {section.content}
                  </ReactMarkdown>
                </MarkdownContainer>
                {section.attachments && section.attachments.length > 0 && (
                  <AttachmentList>
                    {section.attachments.map(attachment => (
                      <AttachmentItem key={attachment.id}>
                        {attachment.name}
                      </AttachmentItem>
                    ))}
                  </AttachmentList>
                )}
              </SectionItem>
            ))}
          </SectionList>
        </>
      );
    }

    if (loadingQuestions) {
      return (
        <EmptyState>
          <EmptyTitle>{t('surgeryDetail.rightPanel.qa_loading')}</EmptyTitle>
          <EmptyDescription>{t('surgeryDetail.rightPanel.qa_loading_desc')}</EmptyDescription>
        </EmptyState>
      );
    }

    const patient = getPatientInfo(selectedPatientForQA);
    const session = patient ? getPatientSession(patient.name) : null;
    const assignedDoctor = session?.doctorId && session.doctorId !== 'general' 
      ? doctors.find(d => d.id === session.doctorId) 
      : null;
    
    if (!patient) return null;

    if (!selectedPatientQuestions || selectedPatientQuestions.questions.length === 0) {
      return (
        <QAContainer>
          <QAHeader>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <PatientNameHeader>{t('surgeryDetail.rightPanel.qa_title', { name: patient.name })}</PatientNameHeader>
              <QAStats>
                <strong>{t('surgeryDetail.rightPanel.qa_conditions')}</strong>
                <div style={{ marginBottom: '4px' }}></div>
                {formatSpecialConditions(session?.specialConditions)}
              </QAStats>
              <QAStats>
                <strong>{t('surgeryDetail.rightPanel.qa_doctor')}:</strong> {assignedDoctor ? t('surgeryDetail.leftPanel.doctor_specific', { name: assignedDoctor.name }) : t('surgeryDetail.rightPanel.qa_doctor_general')}
              </QAStats>
            </div>
            <BackButton onClick={() => setSelectedPatientForQA(null)}>
              {t('surgeryDetail.rightPanel.qa_backButton')}
            </BackButton>
          </QAHeader>
          <EmptyState>
            <EmptyTitle>{t('surgeryDetail.rightPanel.qa_empty_title')}</EmptyTitle>
            <EmptyDescription>
              {t('surgeryDetail.rightPanel.qa_empty_desc_line1', { name: patient.name })}<br />
              {t('surgeryDetail.rightPanel.qa_empty_desc_line2')}<br /><br />
              {t('surgeryDetail.rightPanel.qa_empty_desc_line3')}<br />
              {t('surgeryDetail.rightPanel.qa_empty_desc_line4')}
            </EmptyDescription>
          </EmptyState>
        </QAContainer>
      );
    }

    const qaSets = convertToQASets(selectedPatientQuestions);

    return (
      <QAContainer>
        <QAHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <PatientNameHeader>{t('surgeryDetail.rightPanel.qa_title', { name: patient.name })}</PatientNameHeader>
            <QAStats>
              <strong>{t('surgeryDetail.rightPanel.qa_conditions')}</strong>
              <div style={{ marginBottom: '4px' }}></div>
              {formatSpecialConditions(session?.specialConditions)}
            </QAStats>
            <QAStats>
              <strong>{t('surgeryDetail.rightPanel.qa_doctor')}:</strong> {assignedDoctor ? t('surgeryDetail.leftPanel.doctor_specific', { name: assignedDoctor.name }) : t('surgeryDetail.rightPanel.qa_doctor_general')} | 
              {t('surgeryDetail.rightPanel.qa_stats', { count: qaSets.length })}
              {selectedPatientQuestions.createdAt && 
                ` | ${t('surgeryDetail.rightPanel.qa_firstQuestion', { date: formatDate(selectedPatientQuestions.createdAt) })}`
              }
            </QAStats>
          </div>
          <BackButton onClick={() => setSelectedPatientForQA(null)}>
            {t('surgeryDetail.rightPanel.qa_backButton')}
          </BackButton>
        </QAHeader>

        <QAList>
          {qaSets.map((qaSet) => (
            <QASetItem key={qaSet.index}>
              <QASetHeader>
                <QASetNumber>{t('surgeryDetail.rightPanel.qa_set_title', { index: qaSet.index })}</QASetNumber>
              </QASetHeader>
              
              <QuestionSection>
                <QLabel>{t('surgeryDetail.rightPanel.qa_set_question')}</QLabel>
                <QText>{qaSet.question}</QText>
              </QuestionSection>
              
              <AnswerSection>
                <ALabel>{t('surgeryDetail.rightPanel.qa_set_answer')}</ALabel>
                <AText>{qaSet.answer}</AText>
              </AnswerSection>
            </QASetItem>
          ))}
        </QAList>
      </QAContainer>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal onClick={onClose}>
        <ModalContent onClick={e => e.stopPropagation()} style={{ width: '90vw', maxWidth: '1200px' }}>
          <CloseButton onClick={onClose}>×</CloseButton>
          <h2 style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}>
            {t('surgeryDetail.title', { name: surgery.name })}
          </h2>
          
          <ContentArea>
            <LeftPanel>
              <SectionTitle>{t('surgeryDetail.leftPanel.patientListTitle', { count: surgery.patientIds.length })}</SectionTitle>
              {loadingSessions && (
                <div style={{ textAlign: 'center', padding: '10px', color: '#6b7280' }}>
                  {t('surgeryDetail.leftPanel.sessionLoading')}
                </div>
              )}
              <PatientList>
                {surgery.patientIds.map(patientId => {
                  const patient = getPatientInfo(patientId);
                  const session = patient ? getPatientSession(patient.name) : null;
                  if (!patient) return null;
                  const link = generatePatientLink(patientId);
                  const isSelected = selectedPatientForQA === patientId;
                  
                  return (
                    <PatientItem key={patientId} isSelected={isSelected}>
                      <PatientHeader>
                        <PatientName>{patient.name}</PatientName>
                        <EyeButton onClick={() => handlePatientEyeClick(patientId)}>
                          기록
                        </EyeButton>
                      </PatientHeader>
                      
                      <LinkButtonContainer>
                        <DoctorSelect
                          value={session?.doctorId || 'general'}
                          onChange={(e) => handleDoctorAssignment(patient.name, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="general">{t('surgeryDetail.leftPanel.doctor_general')}</option>
                          {doctors.map(doctor => (
                            <option key={doctor.id} value={doctor.id}>
                              {t('surgeryDetail.leftPanel.doctor_specific', { name: doctor.name })}
                            </option>
                          ))}
                        </DoctorSelect>
                        <CopyButton onClick={() => copyToClipboard(link)}>
                          {t('common.copyLink')}
                        </CopyButton>
                        <OpenLinkButton onClick={() => openLink(link)}>
                          {t('common.openLink')}
                        </OpenLinkButton>
                      </LinkButtonContainer>
                    </PatientItem>
                  );
                })}
              </PatientList>

              <AddPatientSection>
                <SectionTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {t('surgeryDetail.leftPanel.addPatientTitle')}
                  <SmallButton type="button" onClick={() => setIsAddOpen((v) => !v)}>
                    {isAddOpen ? t('common.close') : t('common.open')}
                  </SmallButton>
                </SectionTitle>

                {isAddOpen && (
                  <>
                    <SearchInput
                      type="text"
                      placeholder={t('surgeryDetail.leftPanel.addPatientSearchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <AvailablePatientsList>
                      {filteredAvailablePatients.map(patient => (
                        <AvailablePatientItem
                          key={patient.id}
                          onClick={() => toggleNewPatientSelection(patient.id)}
                          style={{
                            backgroundColor: selectedNewPatients.includes(patient.id) ? '#f0f9ff' : 'white',
                            borderColor: selectedNewPatients.includes(patient.id) ? '#0369a1' : '#e5e7eb'
                          }}
                        >
                          <div style={{ fontWeight: '600' }}>{patient.name}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                            {t('dashboard.patientCard.nickname')}: {patient.name}
                          </div>
                        </AvailablePatientItem>
                      ))}
                    </AvailablePatientsList>

                    {selectedNewPatients.length > 0 && (
                      <Button
                        variant="primary"
                        onClick={handleAddPatients}
                        style={{ marginTop: '12px', width: '100%' }}
                      >
                        {t('surgeryDetail.leftPanel.addSelectedPatients', { count: selectedNewPatients.length })}
                      </Button>
                    )}
                  </>
                )}
              </AddPatientSection>
            </LeftPanel>
            
            <RightPanel>
              {renderRightPanelContent()}
            </RightPanel>
          </ContentArea>

          <ButtonGroup>
            <Button onClick={onClose}>
              {t('common.close')}
            </Button>
            <Button variant="primary" onClick={() => setIsEditModalOpen(true)}>
              {t('surgeryDetail.editSurgery')}
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>

      <SurgeryFormModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSurgeryAdded={() => {
          setIsEditModalOpen(false);
          onSurgeryUpdated();
        }}
        surgery={surgery}
      />
    </>
  );
};

export default SurgeryDetailModal;